/* eslint-disable no-case-declarations */
import { Injectable, inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { select, Store } from '@ngxs/store';
import {
  UserCertificateRoutes,
  UserDocumentsRoutes,
  UserDrillsRoutes,
  UserMaintenanceRoutes,
  UserMedicineRoutes,
  UserNonConformancesRoutes,
  UserProfileRoutes,
  UserRiskAssessmentRoutes,
  UserRunTimeRoutes,
  UserTasksRoutes,
  UserWorkAndRestRoutes,
} from 'src/app/core/enums/routes.enum';
import { IUserAccess } from 'src/app/shared/user-view/models/tenant-user-access.model';
import {
  ALL_DEPS_TOKEN,
  TenantUserAccessState,
} from 'src/app/tenant-selector/state/tenant-user-access.state';
import {
  DOCUMENT_STATUS_LABELS,
  DocumentStatus,
} from 'src/app/document-user/enums/document-status.enum';
import { IDueListCompetence, IDueListItem, DueListModules } from '../models/due-list-item.model';
import { ChangeCurrentSelectedDepartment } from '../../tenant-selector/state/tenant-user-access.actions';
import { SaveLastDueListItem, ClearLastDueListItem } from '../state/due-list.actions';

@Injectable({
  providedIn: 'root',
})
export class DueListRoutingLogicService {
  private readonly injector = inject(Injector);

  userAccess = select(TenantUserAccessState.userAccess);
  currentUserId: string;
  lastItem: IDueListItem | undefined;

  constructor(
    private router: Router,
    private store: Store
  ) {
    toObservable(this.userAccess, { injector: this.injector }).subscribe(
      (userAccess: IUserAccess) => {
        this.currentUserId = userAccess?.id;
      }
    );
  }

  findAppropriateItem(id: string, items: IDueListItem[]): void {
    const item = items.find((i: IDueListItem) => i.id === id) as IDueListItem;
    this.saveLastItem(item);
    this.routeToAppropriateItem(item);
  }

  routeToAppropriateItem(item: IDueListItem): void {
    switch (item.category) {
      case DueListModules.Certificate:
        this.navigateToItem(`${UserCertificateRoutes.Main}/${item.id}`, item.departmentId);
        break;
      case DueListModules.Medicine:
        this.navigateToItem(`${UserMedicineRoutes.Overview}/${item.id}`, item.departmentId);
        break;
      case DueListModules.MaintenanceJob:
        this.navigateToItem(
          `${UserMaintenanceRoutes.Component}/${item.componentId}/jobs/${item.id}`,
          item.departmentId
        );
        break;
      case DueListModules.Runtime:
        this.navigateToItem(`${UserRunTimeRoutes.Edit}/${item.id}`, item.departmentId);
        break;
      case DueListModules.Document:
        const statusRoute =
          item.status.toLowerCase() === DOCUMENT_STATUS_LABELS[DocumentStatus.Expired] ||
          item.status.toLowerCase() === DOCUMENT_STATUS_LABELS[DocumentStatus.Expiring]
            ? DOCUMENT_STATUS_LABELS[DocumentStatus.Published]
            : item.status;
        this.navigateToItem(
          `${UserDocumentsRoutes.Main}/${statusRoute}/${item.id}`,
          item.departmentId
        );
        break;
      case DueListModules.NonConformance:
        this.navigateToItem(
          UserNonConformancesRoutes.ShowOverview.replace(':id', item.id),
          item.departmentId
        );
        break;
      case DueListModules.NonConformanceSubtask:
        this.navigateToItem(
          UserNonConformancesRoutes.SubtasksEdit.replace(
            ':id',
            item.nonConformanceId || ''
          ).replace(':subtaskId', item.id),
          item.departmentId
        );
        break;
      case DueListModules.UserCompetence:
        const route =
          (item as IDueListCompetence).isRegistrationCompleted &&
          (item as IDueListCompetence).hasUserThisCompetence
            ? UserProfileRoutes.CompetenceUser
            : UserProfileRoutes.CompetenceUserRegister;

        this.navigateToItem(`${route}${this.currentUserId}/${item.id}`, item.departmentId);
        break;
      case DueListModules.Drill:
        this.navigateToItem(`${UserDrillsRoutes.Edit}/${item.id}`, item.departmentId);
        break;
      case DueListModules.Work:
        this.navigateToItem(`${UserTasksRoutes.Edit}/${item.id}`, item.departmentId);
        break;
      case DueListModules.RiskAssessment:
        this.navigateToItem(`${UserRiskAssessmentRoutes.Overview}/${item.id}`, item.departmentId);
        break;
      case DueListModules.SignOffPeriod:
        this.navigateToItem(
          `${UserWorkAndRestRoutes.SignOff}`,
          item.departmentId,
          item.userId ? { userId: item.userId } : undefined
        );
        break;
      case DueListModules.AdditionalTime:
        this.navigateToItem(`${UserWorkAndRestRoutes.WorkApproval}`, item.departmentId);
        break;
      case DueListModules.WorkSchedule:
        this.navigateToItem(
          `${UserWorkAndRestRoutes.WorkOverviewDetails}/${
            item.userId
          }/${new Date().getMonth()}/${new Date().getFullYear()}`,
          item.departmentId
        );
        break;
      default:
        throw new Error();
    }
  }

  changeSelectedDepartment(departmentId: string) {
    this.store.dispatch(new ChangeCurrentSelectedDepartment(departmentId, false));
  }

  navigateToItem(url: string, departmentId?: string, queryParams?: Record<string, any>) {
    if (
      this.store.selectSnapshot(TenantUserAccessState.currentDepartmentId) === ALL_DEPS_TOKEN &&
      departmentId
    ) {
      this.changeSelectedDepartment(departmentId);
    }
    if (queryParams) {
      this.router.navigate([url], { queryParams });
    } else {
      this.router.navigateByUrl(url);
    }
  }

  saveLastItem(item: IDueListItem): void {
    this.store.dispatch(new SaveLastDueListItem(item));
  }

  clearLastItem(): void {
    this.store.dispatch(new ClearLastDueListItem());
  }
}
